import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import { MessageSquare, RefreshCw, Send, Clock, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const AdminSupportPage = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyText, setReplyText] = useState('');

    useEffect(() => { fetchTickets(); }, [statusFilter]);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const params = statusFilter ? `?status=${statusFilter}` : '';
            const res = await api.get(`/admin/support-tickets${params}`);
            setTickets(res.data.tickets || []);
        } catch (error) {
            toast.error('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    const handleReply = async () => {
        if (!replyText.trim()) return;
        try {
            await api.post(`/admin/support-tickets/${selectedTicket.id}/reply`, { message: replyText });
            toast.success('Reply sent');
            setReplyText('');
            fetchTickets();
            setSelectedTicket(prev => ({ ...prev, messages: [...(prev.messages || []), { sender: 'admin', message: replyText, timestamp: new Date() }] }));
        } catch (error) {
            toast.error('Failed to send reply');
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.put(`/admin/support-tickets/${id}/status`, { status });
            toast.success('Status updated');
            fetchTickets();
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    const getStatusBadge = (status) => {
        const styles = { open: 'bg-yellow-500/20 text-yellow-400', in_progress: 'bg-blue-500/20 text-blue-400', resolved: 'bg-green-500/20 text-green-400', closed: 'bg-gray-500/20 text-gray-400' };
        const icons = { open: <Clock size={14} />, in_progress: <AlertTriangle size={14} />, resolved: <CheckCircle size={14} />, closed: <XCircle size={14} /> };
        return <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${styles[status] || styles.open}`}>{icons[status]}{status?.replace('_', ' ')}</span>;
    };

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><MessageSquare className="text-purple-400" /> Support Tickets</h1>
                        <p className="text-gray-400 text-sm">Manage user support requests</p>
                    </div>
                    <div className="flex gap-2">
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                            <option value="">All Status</option>
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                        </select>
                        <button onClick={fetchTickets} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"><RefreshCw size={18} /></button>
                    </div>
                </div>
            </div>

            <div className="flex h-[calc(100vh-140px)]">
                {/* Ticket List */}
                <div className="w-1/3 border-r border-slate-700 overflow-y-auto">
                    {loading ? (
                        <div className="text-center py-12 text-gray-400">Loading...</div>
                    ) : tickets.length === 0 ? (
                        <div className="text-center py-16 text-gray-400"><MessageSquare size={48} className="mx-auto mb-4" /><p>No tickets</p></div>
                    ) : (
                        tickets.map(t => (
                            <div key={t.id} onClick={() => setSelectedTicket(t)} className={`p-4 border-b border-slate-700 cursor-pointer hover:bg-slate-700/50 ${selectedTicket?.id === t.id ? 'bg-slate-700' : ''}`}>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-white font-medium">{t.subject}</h3>
                                        <p className="text-gray-400 text-sm">{t.user?.email}</p>
                                    </div>
                                    {getStatusBadge(t.status)}
                                </div>
                                <p className="text-gray-500 text-xs mt-2">{new Date(t.createdAt).toLocaleDateString()}</p>
                            </div>
                        ))
                    )}
                </div>

                {/* Ticket Detail */}
                <div className="flex-1 flex flex-col">
                    {selectedTicket ? (
                        <>
                            <div className="p-4 border-b border-slate-700 bg-slate-800">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h2 className="text-xl font-bold text-white">{selectedTicket.subject}</h2>
                                        <p className="text-gray-400 text-sm">{selectedTicket.user?.email} • {selectedTicket.category}</p>
                                    </div>
                                    <select value={selectedTicket.status} onChange={e => updateStatus(selectedTicket.id, e.target.value)} className="px-3 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm">
                                        <option value="open">Open</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="resolved">Resolved</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {(selectedTicket.messages || []).map((m, i) => (
                                    <div key={i} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[70%] p-3 rounded-lg ${m.sender === 'admin' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-gray-200'}`}>
                                            <p>{m.message}</p>
                                            <p className="text-xs opacity-70 mt-1">{new Date(m.timestamp).toLocaleString()}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="p-4 border-t border-slate-700 bg-slate-800">
                                <div className="flex gap-2">
                                    <input type="text" placeholder="Type your reply..." value={replyText} onChange={e => setReplyText(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleReply()} className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                                    <button onClick={handleReply} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500"><Send size={18} /></button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-400">Select a ticket to view</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminSupportPage;
